import th from '@tillhub/javascript-sdk'
import router from '../router/index.js'
import SafeGet from 'just-safe-get'
import { formatDistanceToNowStrict } from 'date-fns'
import { getBaseUrl } from '@shared/utils/baseUrlProvider.js'
import { useKeycloak, getToken } from '@josempgon/vue-keycloak'
import { isUnifiedCommerce, isKeycloackEnabled } from '@shared/constants'
import jwt_decode from 'jwt-decode'
import * as Sentry from '@sentry/vue'
import {
  isMerchantSSODomain,
  buildSSOLogoutUrl,
  getSSOConfig
} from '@uz/config'
import { usePosthog, FeatureFlags } from '@shared/plugins/posthog'
import {
  clearSupportIdpHint,
  consumeSupportIdpHint,
  getRedirectUri
} from '@shared/plugins/keycloak'
import { useConfigurationStore } from '@shared/store/configuration'
import { useAuthStore } from '@shared/store/auth'
import i18n from '@shared/i18n'
import { env } from '@shared/config/env'

const t = i18n.global.t
const defaultKeycloakLocale = 'en'
const supportedKeycloakLocales = new Set(['de', 'en'])

function getDashboardLocale() {
  return (
    i18n?.global?.locale?.value ||
    i18n?.global?.locale ||
    globalThis?.navigator?.language ||
    ''
  )
}

function getKeycloakLocale() {
  const localeValue = getDashboardLocale()
  const normalizedLocale = String(localeValue).replace('_', '-').toLowerCase()
  const baseLocale = normalizedLocale.split('-')[0]

  if (supportedKeycloakLocales.has(baseLocale)) {
    return baseLocale
  }

  return defaultKeycloakLocale
}

const isProduction = env.nodeEnv === 'production'
// How long to wait for the plugin to build the adapter before giving up on it.
const adapterTimeout = 5000

const isKeycloakSession = isUnifiedCommerce() && isKeycloackEnabled()

// Global promise to ensure only one retry after 2FA completes
let pending2FAPromise = null

// Global flag to prevent multiple session expired modals
let isSessionExpiredModalOpen = false

// `setItem` stringifies, so a token the adapter does not have would be stored
// as the word "undefined" and read back later as if it were real.
function persistKeycloakToken(key, value) {
  if (value) {
    globalThis.localStorage.setItem(key, value)
    return
  }

  globalThis.localStorage.removeItem(key)
}

// Global function to store Keycloak tokens
function storeKeycloakToken() {
  const { keycloak } = useKeycloak()
  const { token, refreshToken, idToken } = keycloak.value

  persistKeycloakToken('token', token)
  persistKeycloakToken('refreshToken', refreshToken)
  persistKeycloakToken('idToken', idToken)
}

export const initFactory = (base, token, user) => {
  const obj = { base }
  if (token) {
    obj.credentials = { token }
  }
  if (user) {
    obj.user = user
  }
  // We need a way for API to recognize that its a unified commerce client, so this will add the x-whitelabel to the header request
  if (isKeycloakSession) {
    obj.whitelabel = 'unified-commerce'
  }

  /**
   * Error response interceptor for the SDK. It is called with an Axios error response, whenever the SDK receives one.
   * Check here for its schema: https://github.com/axios/axios#response-schema
   * @param {AxiosResponse} resp - error response received by the SDK
   * @returns {Promise<never>} - will always return a rejected promise
   */
  const interceptor = async (resp) => {
    const statusCode = SafeGet(resp, 'response.status', 0)
    // it's probably best to use whitelist of codes, otherwise me might reroute when we don't want to, e.g. 404 or 422
    // FIXME 401 has been removed for now, as it breaks a lot of e2e tests. Please readd after workaround has been found
    const errorCodes = [504]
    // Handle 2FA needed error
    if (is2FAAuthenticationRequired(resp)) {
      // Store the original token in local storage
      const { keycloak } = useKeycloak()
      try {
        await keycloak.value.updateToken(-1)
      } catch {
        // Keycloak session already expired server-side; silent refresh is not
        // possible (checkLoginIframe is disabled). Reject cleanly instead of
        // leaving an unhandled promise rejection that reaches Sentry.
        useAuthStore().logout()
        router.replace('/logout')
        return Promise.reject(resp)
      }
      storeKeycloakToken()

      const originalRequest = resp.config

      // If there's already a pending 2FA promise, add this request to the queue
      // and return a promise that will retry this specific request when 2FA completes
      if (pending2FAPromise) {
        return pending2FAPromise.then(async (newToken) => {
          return th.http.getClient().request({
            ...originalRequest,
            method: originalRequest.method,
            headers: {
              ...originalRequest.headers,
              Authorization: `Bearer ${newToken}`
            }
          })
        })
      }

      let channel
      let checkClosedInterval
      let promiseReject

      pending2FAPromise = new Promise((resolve, reject) => {
        promiseReject = reject
        channel = new BroadcastChannel('2fa_reinitialize')
        channel.addEventListener('message', async (event) => {
          try {
            // Get new token after 2FA
            const newToken = event.data?.token

            // Clear the interval to prevent race condition
            if (checkClosedInterval) {
              clearInterval(checkClosedInterval)
            }

            channel.close()

            pending2FAPromise = null
            resolve(newToken)
          } catch (error) {
            reject(error)
            pending2FAPromise = null
          }
        })
      })

      const width = 600
      const height = 700
      const y = window.top.outerHeight / 2 + window.top.screenY - height / 2
      const x = window.top.outerWidth / 2 + window.top.screenX - width / 2

      const popupWindow = window.open(
        globalThis.location.origin + '/2fa_required',
        'Keycloak Login',
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${y}, left=${x}`
      )

      // Add listener to detect when popup is closed
      if (popupWindow) {
        checkClosedInterval = setInterval(() => {
          if (popupWindow.closed) {
            clearInterval(checkClosedInterval)
            if (promiseReject) {
              const errorMessage = t('pages.auth.login.2fa.cancelled_by_user')
              const error = new Error(errorMessage)
              error.cancelled = true
              promiseReject(error)
              pending2FAPromise = null
            }
            if (channel) {
              channel.close()
            }
          }
        }, 1000) // Check every second
      }

      // Check if popup was blocked and show notification
      if (
        !popupWindow ||
        popupWindow.closed ||
        typeof popupWindow.closed === 'undefined'
      ) {
        // Popup was blocked, show notification to user
        setTimeout(() => {
          if (!globalThis.app.show2FAPopupBlocked) {
            globalThis.app.show2FAPopupBlocked = true
            const app = globalThis.app.config.globalProperties
            app.$notify.warning({
              title: t('pages.auth.login.2fa.popup_blocked.title'),
              message: t('pages.auth.login.2fa.popup_blocked.message')
            })
            setTimeout(() => {
              globalThis.app.show2FAPopupBlocked = false
            }, 1000)
          }
        }, 1000) // Delay to ensure popup check is accurate
      }

      // Return a promise that will retry this specific request when 2FA completes
      return pending2FAPromise.then(async (newToken) => {
        return th.http.getClient().request({
          ...originalRequest,
          method: originalRequest.method,
          headers: {
            ...originalRequest.headers,
            Authorization: `Bearer ${newToken}`
          }
        })
      })
    }

    if (isKeycloackEnabled()) {
      try {
        const { keycloak } = useKeycloak()
        const decodedToken = jwt_decode(keycloak.value.token)
        if (decodedToken.exp + 25 * 60 * 1000 < Date.now() / 1000) {
          useAuthStore().logout()
          router.replace('/logout')
        }
      } catch {}
    }
    // A request that shows its own error state opts out of the error-page redirects below
    if (resp?.config?.skipErrorRedirect) return Promise.reject(resp)

    // Taking out 403 for keycloak
    if (!isKeycloackEnabled()) errorCodes.push(403)
    // Logout keycloak if 401 (Unauthorized)
    if (isKeycloackEnabled() && statusCode === 401) {
      useAuthStore().logout()
      router.replace('/logout')
    }
    if (errorCodes.includes(statusCode)) {
      router.push(`/${statusCode}`)
    }

    // NOTE always reject with original argument when present, see:
    // https://github.com/axios/axios#interceptors
    // Avoid rejecting with undefined — that surfaces as unhandled non-Error rejections in Sentry.
    return Promise.reject(
      resp ??
        new Error('Tillhub SDK: response interceptor received empty error')
    )
  }

  obj.responseInterceptors = [interceptor]

  return obj
}

export const getCredentials = () => {
  const token = globalThis.localStorage.getItem('token') || ''
  const decoded = token ? jwt_decode(token) : null
  const user =
    decoded?.legacy_id || decoded?.clientAccountId || decoded?.sub || ''
  return { user, token }
}

export const getConnectedDashboards = () => {
  const token = globalThis.localStorage.getItem('token') || ''
  if (!token) return null
  try {
    const decoded = jwt_decode(token)
    const map = decoded?.connectedDashboards
    if (!map || typeof map !== 'object' || !Object.keys(map).length) {
      return null
    }
    return {
      map,
      activeUnzerId: decoded.unzerId
    }
  } catch {
    return null
  }
}

export const getUnzerId = () => {
  const token = globalThis.localStorage.getItem('token') || ''
  if (!token) return ''
  try {
    const decoded = jwt_decode(token)
    return decoded?.unzerId || ''
  } catch {
    return ''
  }
}

export const getIsFirstLoginDone = () => {
  const token = globalThis.localStorage.getItem('token') || ''
  if (!token) return true
  try {
    const decoded = jwt_decode(token)
    return (
      decoded?.isFirstLoginDone === true || decoded?.isFirstLoginDone === 'true'
    )
  } catch {
    return true
  }
}

export const getUserId = () => {
  return globalThis.localStorage.getItem('keycloakUserId')
}

export {
  getClientAccountId,
  getActiveClientAccountId
} from './client-account-id.js'
export {
  fetchIamUserProfile,
  isViewingSwitchedDashboard
} from './iam-user-profile.js'

/**
 * Global login function for external services
 * NOTE: This function is used by iOS POS.
 *
 * @param {String} token - jwt access token
 * @param {String} user - account uuid
 * @param {String} url - url to redirect to (optional)
 * @returns {Boolean|Error} returns boolean or throws error
 */
globalThis.applicationLogin = function (token, user, url) {
  const authStore = useAuthStore()
  if (authStore.isAuthenticated) {
    // eslint-disable-next-line no-console
    console.log('[AUTH] Already logged in!')
    if (url) router.replace(url)
    return true
  }
  if (!token) throw new Error('Token is required for login')
  if (!user) throw new Error('User is required for login')
  if (typeof token !== 'string') throw new Error('Token must be JWT string')
  if (typeof user !== 'string')
    throw new Error('User must be account uuid string')
  if (url && typeof url !== 'string') throw new Error('Url must be string')

  // Navigate to login to avoid issues
  router.replace('/login')

  // Set max expire date, because exp does not exist in the JWT token
  const date = new Date()
  date.setMonth(date.getMonth() + 3)
  const expiresAt = date

  authStore.applyApplicationLoginSession({ token, user, expiresAt })

  // Set session
  new AuthService()
    .setSession({ token, expiresAt, user })
    .then(async function () {
      // Fetch me data
      await authStore.getMeData()
      authStore.setOwnerRoleAndAllScopes()

      // Navigate back
      router.replace(url ? `/redirect?redirect=${url}` : '/redirect')
    })

  return true
}

/**
 * Global logout function for external services
 *
 * @returns {void} on return
 */
globalThis.applicationLogout = function () {
  useAuthStore().logout()
  router.replace('/logout')
}

class AuthService {
  constructor() {
    this.expiresAt = null
    this.expiresAtTimeout = null
    this.broadcastChannel = new BroadcastChannel('keycloak_auth_channel')

    this.handle2FASuccess = this.handle2FASuccess.bind(this)
    this.broadcastChannel.addEventListener('message', this.handle2FASuccess)
  }

  async handle2FASuccess(event) {
    const { token } = event.data
    if (!token) {
      Sentry.captureException(new Error('No token found after 2FA'))
      return
    }

    // Unblock the pending API retry, then downgrade session to al1 (no persisted step-up token).
    const broadcastChannel = new BroadcastChannel('2fa_reinitialize')
    broadcastChannel.postMessage({
      type: '2fa_reinitialize',
      token
    })
    broadcastChannel.close()

    await this.exchange2FATokenForLowerACR(token)
  }

  /*
    Clear token expire timeout
  */
  clearExpiresAtTimeout() {
    this._log('clear timeout')
    clearTimeout(this.expiresAtTimeout)
  }

  handleLogout() {
    this._log('token expired!')
    useAuthStore().logout()
    router.replace('/login')
  }

  showSessionExpiredModal() {
    if (isSessionExpiredModalOpen) {
      return
    }

    const posthog = usePosthog()
    const has2FAFeature = posthog.isFeatureEnabled(FeatureFlags.UOD_2FA_FEATURE)

    if (!has2FAFeature) {
      this.handleLogout()
      return
    }

    const thModal = globalThis.app?.config?.globalProperties?.$thModal
    if (thModal) {
      isSessionExpiredModalOpen = true
      thModal.show('session-expired-modal')
    } else {
      this.handleLogout()
    }
  }

  resetSessionExpiredModalFlag() {
    isSessionExpiredModalOpen = false
  }

  getSessionTimeoutMinutes() {
    try {
      const refreshToken = globalThis.localStorage.getItem('refreshToken')
      if (!refreshToken) return 15

      const decoded = jwt_decode(refreshToken)
      const timeoutSeconds = decoded.exp - decoded.iat
      const timeoutMinutes = Math.round(timeoutSeconds / 60)

      return timeoutMinutes
    } catch {
      return 15
    }
  }

  /*
    Set logout timeout that logs out the user after token expires
  */
  async setExpiresAtTimeout() {
    if (!isKeycloakSession) {
      this.clearExpiresAtTimeout()
    }
    const expiresAtCookie = globalThis.localStorage.getItem('expires_at')
    if (expiresAtCookie) {
      this.expiresAt = JSON.parse(expiresAtCookie).date
      let diff = new Date(this.expiresAt) - new Date()
      this._log(
        'token expires in',
        formatDistanceToNowStrict(new Date(this.expiresAt))
      )
      // Check if expires at is later than now
      if (diff <= 0) {
        // Logout because token has expired
        this.handleLogout()
      } else {
        // setTimeout max delay
        const maxDelay = 2147483647
        // Replace time difference if larger than max delay. If diff is larger than max delay setTimeout will fire immediately
        if (diff > maxDelay) diff = maxDelay
        this.expiresAtTimeout = setTimeout(() => {
          // Token expired, logout
          this.handleLogout()
        }, diff)
      }
    } else {
      // we dont handle expire timeout for keycloak yet
      if (isKeycloakSession) return
      // Expires at data not set, "probably" not logged in
      else useAuthStore().logout()
    }
  }

  async handleAuthentication(authResult) {
    await this.setSession(authResult)
  }

  async refreshKeycloakToken() {
    const { keycloak } = useKeycloak()
    if (!keycloak?.value?.token) {
      return
    }

    const { updateToken } = keycloak.value
    const decodedToken = jwt_decode(keycloak.value.token)

    if (decodedToken.acr === 'al2') {
      await this.exchange2FATokenForLowerACR(keycloak.value.token)
      return
    }

    try {
      // Setting updateToken to -1 will always refresh the token
      const refreshed = await updateToken(-1)
      // If we have token that means that it was refreshed
      if (refreshed) {
        const token = await getToken()
        const decoded = jwt_decode(token)
        // Forced refresh can re-elevate to al2 from a stepped-up KC session —
        // never persist or use that for the SDK; downgrade instead.
        if (decoded.acr === 'al2') {
          await this.exchange2FATokenForLowerACR(token)
          return
        }
        globalThis.localStorage.setItem('keycloakUserId', decoded?.sub)
        storeKeycloakToken()
        const baseUrl = await getBaseUrl()
        const user =
          globalThis.localStorage.getItem('clientAccountId') ||
          decoded?.legacy_id ||
          decoded?.clientAccountId ||
          useAuthStore().me?.user?.id
        this.initializeSDK(baseUrl, token, user)
      }
    } catch {
      // token could not be refreshed, time to logout
      if (isUnifiedCommerce()) {
        this.showSessionExpiredModal()
      } else {
        this.handleLogout()
      }
    }
  }

  async setSession(authResult) {
    const authStore = useAuthStore()
    authStore.setInitialized(false)
    const baseUrl = await getBaseUrl()
    // Set the time that the access token will expire at
    const expiresAt = JSON.stringify({
      date: authResult.expiresAt
    })

    globalThis.localStorage.setItem('token', authResult.token)
    globalThis.localStorage.setItem('expires_at', expiresAt)
    authStore.setAuthenticated(!!authResult.token)
    this.setExpiresAtTimeout()

    th.init(initFactory(baseUrl, authResult.token, authResult.user))
    authStore.setInitialized(true)
  }

  async login({ username, password, organisation, recaptchaToken }) {
    if (organisation) {
      return th.auth.loginWithOrganisation({
        organisation,
        username,
        password,
        recaptcha_token: recaptchaToken
      })
    }
    const {
      token,
      expiresAt,
      user,
      features,
      name,
      scopes,
      role,
      orgName,
      whitelabel
    } = await th.auth.loginUsername({
      username,
      password,
      recaptcha_token: recaptchaToken
    })

    return {
      token,
      expiresAt,
      user,
      features,
      name,
      scopes,
      role,
      orgName,
      whitelabel
    }
  }

  async loginAsSupport({ token: supportToken, clientAccount, recaptchaToken }) {
    const {
      token,
      expiresAt,
      user,
      features,
      name,
      username,
      is_support: isSupport,
      role,
      scopes,
      whitelabel
    } = await th.auth.loginAsSupport({
      token: supportToken,
      client_account: clientAccount,
      recaptcha_token: recaptchaToken
    })

    return {
      token,
      expiresAt,
      user,
      features,
      name,
      username,
      isSupport,
      role,
      scopes,
      whitelabel
    }
  }

  async resetPassword({ email, organisation }) {
    if (organisation) {
      const { msg } = await th.auth.requestPasswordResetWithOrganisation({
        email,
        organisation
      })
      return { msg }
    }
    const { msg } = await th.auth.requestPasswordReset({ email })
    return { msg }
  }

  async newPassword(data) {
    const { msg } = await th.auth.setNewPassword(data)

    return { msg }
  }

  captureSsoLoginLatency(isSSOMerchant, loginStartTime, authStore, user) {
    if (!isSSOMerchant || !loginStartTime) {
      return
    }

    const posthog = usePosthog()
    posthog.capture('sso_login_latency', {
      latency: Date.now() - loginStartTime,
      merchant_id: user,
      email: authStore.me?.user?.email
    })
  }

  reportSessionError(err, isSSOMerchant) {
    if (isSSOMerchant) {
      Sentry.captureException(err)
    }

    throw err
  }

  handleMissingKeycloakClientId(user, isSSOMerchant, authStore) {
    if (user) {
      clearSupportIdpHint()
      return false
    }

    const { keycloak } = useKeycloak()

    // Without an adapter there is nothing to log in with. Let startup finish
    // anyway, so the route guards can send the user to the login page instead
    // of the app failing on a login it cannot start.
    if (isSSOMerchant || !keycloak.value) {
      authStore.setInitialized(true)
      return true
    }

    const { login } = keycloak.value
    // A support agent lands here because Endor's response was unusable. The hint
    // returns them through the broker they just authenticated against.
    const idpHint = consumeSupportIdpHint()

    login({
      locale: getKeycloakLocale(),
      redirectUri: getRedirectUri(),
      ...(idpHint ? { idpHint } : {})
    })
    return false
  }

  // `/` is included because the redirect URI is pinned to the origin root, so
  // every finished login lands there. All three are passed through, not stayed on.
  async redirectToHomeAfterKeycloakLogin() {
    if (!['/', '/login', '/401'].includes(globalThis.location.pathname)) {
      return
    }

    await new Promise((resolve) => setTimeout(resolve, 100))
    router.replace('/home')
  }

  async finalizeKeycloakSession({
    authStore,
    baseUrl,
    token,
    user,
    isSSOMerchant,
    loginStartTime
  }) {
    await authStore.getMeData()

    const resolvedUser = authStore.me?.user?.id || user
    const resolvedToken = this.isValid2FAKeycloakToken()
      ? globalThis.localStorage.getItem('token2FA')
      : token

    this.initializeSDK(baseUrl, resolvedToken, resolvedUser)
    await useConfigurationStore().loadTenantConfig()
    await this.redirectToHomeAfterKeycloakLogin()

    this.captureSsoLoginLatency(
      isSSOMerchant,
      loginStartTime,
      authStore,
      resolvedUser
    )
  }

  async fetchAndPersistKeycloakToken() {
    const token = await getToken()
    const decodedToken = jwt_decode(token)
    globalThis.localStorage.setItem('keycloakUserId', decodedToken?.sub)
    if (
      decodedToken?.clientAccountId &&
      !globalThis.localStorage.getItem('clientAccountId')
    ) {
      globalThis.localStorage.setItem(
        'clientAccountId',
        decodedToken.clientAccountId
      )
    }
    storeKeycloakToken()
    return token
  }

  async getSession() {
    const isSSOMerchant = isMerchantSSODomain()
    const loginStartTime = isSSOMerchant ? Date.now() : null

    try {
      const authStore = useAuthStore()
      authStore.setInitialized(false)
      const baseUrl = await getBaseUrl()
      let token, user
      const credentials = getCredentials()
      token = credentials.token
      user = !isKeycloakSession ? credentials.user : undefined
      if (isKeycloakSession) {
        // We add a user to avoid SDK issues, to be replaces by the proper client id on meData request
        user = await new Promise(this.getClientIdKeyCloak.bind(this))
        if (
          this.handleMissingKeycloakClientId(user, isSSOMerchant, authStore)
        ) {
          return
        }
        // This function checks if the token is still valid and will update it if it is expired
        token = await this.fetchAndPersistKeycloakToken()
      }

      if (isKeycloakSession && this.isValid2FAKeycloakToken()) {
        token = globalThis.localStorage.getItem('token2FA')
        const decodedToken = jwt_decode(token)
        user = decodedToken?.clientAccountId
      }

      authStore.setCredentialsFromToken(token)
      authStore.setAuthenticated(!!token)
      this.initializeSDK(baseUrl, token, user)
      if (isKeycloakSession) {
        await this.finalizeKeycloakSession({
          authStore,
          baseUrl,
          token,
          user,
          isSSOMerchant,
          loginStartTime
        })
      } else {
        authStore.setUser(user)
        this.setExpiresAtTimeout()
      }
      authStore.setInitialized(true)
    } catch (err) {
      this.reportSessionError(err, isSSOMerchant)
    }
  }

  async getSessionTwoFA() {
    const { keycloak } = useKeycloak()
    const { login } = keycloak.value

    const loginOptions = {
      locale: getKeycloakLocale(),
      redirectUri: globalThis.location.origin + '/2fa_broadcast',
      acr: {
        values: ['al2'],
        essential: true
      }
    }

    await login(loginOptions)
  }

  initializeSDK(baseUrl, token, user) {
    if (!token) {
      th.init(initFactory(baseUrl))
    } else {
      th.init(initFactory(baseUrl, token, user))
    }
  }

  /**
   * True when a persisted step-up (2FA) access token exists and is not expired.
   */
  isValid2FAKeycloakToken() {
    const tfa = globalThis.localStorage?.getItem('token2FA')
    if (!tfa || typeof tfa !== 'string') {
      return false
    }
    try {
      const decoded = jwt_decode(tfa)
      const exp = decoded?.exp
      if (typeof exp === 'number') {
        return exp * 1000 > Date.now()
      }
      return true
    } catch {
      return false
    }
  }

  stopInterval2FAValidCheck() {
    // Reserved for optional periodic 2FA token checks; no-op if unused.
  }

  /**
   * An adapter that has settled knows who the user is however long it took, and
   * no deadline may throw that answer away: that is what used to send people
   * who already had a Keycloak session back through a full login. The deadline
   * only applies while the adapter itself is missing. `hasFailed` is terminal
   * either way, including when the adapter exists but init never clears
   * `isPending`.
   */
  getClientIdKeyCloak(resolve, reject, deadline = Date.now() + adapterTimeout) {
    const { keycloak, isPending, hasFailed } = useKeycloak()

    if (hasFailed?.value) {
      resolve(keycloak.value?.subject)
      return
    }

    if (keycloak.value) {
      if (!isPending.value) {
        resolve(keycloak.value.subject)
        return
      }
    } else if (Date.now() >= deadline) {
      // The plugin builds the adapter asynchronously, so for a moment there is
      // nothing to read. If it never arrives there is nothing left to wait for,
      // and startup must not hang on it.
      resolve(undefined)
      return
    }

    setTimeout(
      this.getClientIdKeyCloak.bind(this, resolve, reject, deadline),
      100
    )
  }

  async logout() {
    const { keycloak } = useKeycloak()

    const isSSOMerchant = isMerchantSSODomain()

    if (isKeycloakSession && keycloak.value.authenticated) {
      if (isSSOMerchant) {
        const ssoConfig = getSSOConfig()
        const keycloakUrl = keycloak.value.authServerUrl
        const idToken = keycloak.value.idToken

        const dashboardRedirectUri = `${ssoConfig.dashboardUrl}/login`

        useAuthStore().setIsSupport(null)
        this.stopInterval2FAValidCheck()

        globalThis.localStorage.removeItem('token')
        globalThis.localStorage.removeItem('refreshToken')
        globalThis.localStorage.removeItem('idToken')
        globalThis.localStorage.removeItem('keycloakUserId')
        globalThis.localStorage.removeItem('clientAccountId')
        globalThis.localStorage.removeItem('expires_at')
        globalThis.localStorage.removeItem('support_client_account')
        this.userProfile = null
        this.clearExpiresAtTimeout()

        const logoutUrl = buildSSOLogoutUrl(
          keycloakUrl,
          dashboardRedirectUri,
          idToken
        )

        globalThis.location.href = logoutUrl
        return
      } else {
        const { logout } = keycloak.value
        // Spelled with the trailing slash like every other redirect URI we send,
        // so one registered entry in Keycloak covers login and logout alike.
        const logoutOptions = { redirectUri: `${globalThis.location.origin}/` }
        logout(logoutOptions)
      }
    }
    // Set IsSupport to null in case of logout to prevent issues on new login.
    useAuthStore().setIsSupport(null)

    // Clear access token and ID token from local storage
    globalThis.localStorage.removeItem('token')
    globalThis.localStorage.removeItem('token2FA')
    globalThis.localStorage.removeItem('token2FACreationDate')
    globalThis.localStorage.removeItem('user')
    globalThis.localStorage.removeItem('expires_at')
    globalThis.localStorage.removeItem('support_client_account')
    globalThis.localStorage.removeItem('clientAccountId')
    this.userProfile = null
    // the caller must navigate to the home route
    this.clearExpiresAtTimeout()
  }

  isAuthenticated() {
    // Get token from storage
    const token = globalThis.localStorage.getItem('token')
    return !!token
  }

  _log() {
    // eslint-disable-next-line no-console
    if (!isProduction) console.log('debug | auth |', ...arguments)
  }

  /**
   * @param {string} [subjectTokenOverride] - Optional al2 access token to use as token-exchange subject.
   */
  async exchange2FATokenForLowerACR(subjectTokenOverride) {
    try {
      const { keycloak } = useKeycloak()
      const currentToken =
        subjectTokenOverride ||
        keycloak?.value?.token ||
        globalThis.localStorage.getItem('token')

      if (!currentToken) {
        return
      }

      const exchangeUrl = `${keycloak.value.authServerUrl}/realms/${keycloak.value.realm}/protocol/openid-connect/token`

      const response = await fetch(exchangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
          subject_token: currentToken,
          requested_token_type: 'urn:ietf:params:oauth:token-type:access_token',
          client_id: keycloak.value.clientId,
          acr_values: 'al1'
        })
      })

      if (!response.ok) {
        return
      }

      const tokenData = await response.json()

      if (tokenData.access_token) {
        // Store the downgraded (al1) token — do not call refreshKeycloakToken()
        // afterwards: updateToken(-1) re-issues al2 from the stepped-up KC session.
        globalThis.localStorage.setItem('token', tokenData.access_token)
        globalThis.localStorage.removeItem('token2FA')
        globalThis.localStorage.removeItem('token2FACreationDate')
        if (tokenData.refresh_token) {
          globalThis.localStorage.setItem(
            'refreshToken',
            tokenData.refresh_token
          )
        }
        if (tokenData.id_token) {
          globalThis.localStorage.setItem('idToken', tokenData.id_token)
        }

        // Update Keycloak's internal state
        keycloak.value.token = tokenData.access_token
        keycloak.value.tokenParsed = jwt_decode(tokenData.access_token)
        if (tokenData.refresh_token) {
          keycloak.value.refreshToken = tokenData.refresh_token
          keycloak.value.refreshTokenParsed = jwt_decode(
            tokenData.refresh_token
          )
        }
        if (tokenData.id_token) {
          keycloak.value.idToken = tokenData.id_token
          keycloak.value.idTokenParsed = jwt_decode(tokenData.id_token)
        }

        const baseUrl = await getBaseUrl()
        const decoded = jwt_decode(tokenData.access_token)
        const user =
          globalThis.localStorage.getItem('clientAccountId') ||
          decoded?.legacy_id ||
          decoded?.clientAccountId ||
          useAuthStore().me?.user?.id
        this.initializeSDK(baseUrl, tokenData.access_token, user)
      }
    } catch {
      return
    }
  }
}

/**
 * Checks if an error response indicates that 2FA authentication is required
 * @param {Error} error - The error object from a failed request
 * @returns {boolean} - True if the error is a 2FA authentication requirement
 */
export function is2FAAuthenticationRequired(error) {
  return (
    error?.response?.status === 401 &&
    error?.response?.headers?.['www-authenticate']?.includes(
      'insufficient_user_authentication'
    )
  )
}

export default new AuthService()
